import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../../hooks/use-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Phone,
  Send,
  PhoneOff,
  PlayCircle,
  RefreshCw,
  Loader2,
  MessageSquare,
  Bot,
  User,
  CheckCircle,
  AlertCircle,
  Zap,
  Globe,
  Volume2,
  Mic,
  MicOff
} from 'lucide-react';

const LANG_CODES = {
  english: 'en-US',
  french: 'fr-FR',
  mixed: 'en-US',
};

const AIVoiceSimulatorEnhanced = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { makeAuthenticatedRequest, getBackendUrl } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // AudioContext refs
  const audioCtxRef = useRef(null);
  const currentSourceRef = useRef(null);

  // Voice refs — stable across renders, no stale-closure risk
  const voiceModeRef = useRef(false);
  const recognitionRef = useRef(null);
  const selectedLangRef = useRef('en-US');
  const processingRef = useRef(false);
  const currentSessionRef = useRef(null);
  const sendMessageRef = useRef(null);
  const startListeningRef = useRef(null);

  const unlockAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  };

  const playBase64Audio = async (base64, format) => {
    const resumeListening = () => {
      if (voiceModeRef.current && !processingRef.current) {
        setTimeout(() => startListeningRef.current?.(), 350);
      }
    };
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) {
        console.warn('[AIVoice] AudioContext not initialized');
        resumeListening();
        return;
      }
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const audioBuffer = await ctx.decodeAudioData(bytes.buffer);
      if (currentSourceRef.current) {
        try { currentSourceRef.current.stop(); } catch (_) {}
        try { currentSourceRef.current.disconnect(); } catch (_) {}
        currentSourceRef.current = null;
      }
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      source.onended = () => {
        currentSourceRef.current = null;
        setVoiceState('idle');
        textareaRef.current?.focus();
        resumeListening();
      };
      // Resume context if it re-suspended during the decodeAudioData await (Incognito)
      if (ctx.state === 'suspended') await ctx.resume();
      source.start(0);
      currentSourceRef.current = source;
      setVoiceState('speaking');
    } catch (err) {
      console.warn('[AIVoice] AudioContext playback failed, trying SpeechSynthesis:', err.message);
      // Fallback: browser TTS from ai_response text already shown in history
      setVoiceState('idle');
      resumeListening();
    }
  };

  // ── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [customerMessage, setCustomerMessage] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceState, setVoiceState] = useState('idle'); // idle|listening|processing|speaking|error
  const [interimTranscript, setInterimTranscript] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('english');

  const stateData = location.state || {};

  const [callForm, setCallForm] = useState({
    customer_phone: stateData.phoneNumber || '+1234567890',
    customer_name: user?.name || 'Test Customer',
    business_id: stateData.businessId || '',
    business_name: user?.business_name || 'Test Business Corp',
    test_email_override: ''
  });

  useEffect(() => {
    const brandId = currentBusiness?.brandId;
    if (brandId) {
      setCallForm(prev => ({ ...prev, business_id: prev.business_id || brandId }));
    }
  }, [currentBusiness?.brandId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory, interimTranscript]);

  useEffect(() => {
    if (stateData.autoStart && !callActive && !loading) {
      handleInitiateCall();
    }
  }, [stateData.autoStart]);

  // ── STT ──────────────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }
    setInterimTranscript('');
    setVoiceState('idle');
  }, []);

  const startListening = useCallback(() => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) {
      // Kill voice mode so the button does not stay stuck ON in Firefox / non-STT browsers
      voiceModeRef.current = false;
      setVoiceMode(false);
      setVoiceState('idle');
      toast({
        title: t('od.notSupported'),
        description: t('od.speechRequires'),
        variant: 'destructive',
      });
      return;
    }
    // Clean up any existing instance
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) {}
      recognitionRef.current = null;
    }
    setInterimTranscript('');

    const recognition = new SpeechRec();
    recognition.lang = selectedLangRef.current;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setVoiceState('listening');

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      setInterimTranscript(interim || final);
      if (final) {
        setInterimTranscript('');
        // Interrupt AI audio if speaking
        if (currentSourceRef.current) {
          try { currentSourceRef.current.stop(); } catch (_) {}
          try { currentSourceRef.current.disconnect(); } catch (_) {}
          currentSourceRef.current = null;
        }
        sendMessageRef.current?.(final.trim());
        textareaRef.current?.focus();
      }
    };

    recognition.onerror = (e) => {
      if (e.error === 'no-speech') {
        // Transient — restart if still in voice mode
        if (voiceModeRef.current) {
          setTimeout(() => startListeningRef.current?.(), 700);
        }
      } else if (e.error === 'aborted') {
        // Intentional abort (we replaced this instance) — not an error, ignore silently
      } else if (
        e.error === 'not-allowed' ||
        e.error === 'service-not-allowed' ||
        e.error === 'audio-capture'   // Safari / Android: mic hardware inaccessible
      ) {
        // Fatal — kill voice mode so onend does not restart and loop infinitely
        voiceModeRef.current = false;
        setVoiceMode(false);
        setVoiceState('error');
        toast({
          title: t('od.micBlocked'),
          description: t('od.micBlockedDesc'),
          variant: 'destructive',
        });
      } else {
        console.warn('[AIVoice STT] Error:', e.error);
        setVoiceState('error');
      }
    };

    recognition.onend = () => {
      // Guard: only act if this instance is still the active one.
      // Without this, an aborted-and-replaced instance overwrites the new ref
      // and schedules a duplicate restart — causing ghost listeners in Chrome.
      if (recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      if (voiceModeRef.current && !processingRef.current) {
        setTimeout(() => startListeningRef.current?.(), 350);
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
  }, [toast]);

  // Keep stable refs in sync every render
  startListeningRef.current = startListening;

  const toggleVoiceMode = useCallback(() => {
    const next = !voiceModeRef.current;
    voiceModeRef.current = next;
    setVoiceMode(next);
    if (next) {
      unlockAudio();
      startListeningRef.current?.();
    } else {
      stopListening();
    }
  }, [stopListening]);

  // ── Scenarios ─────────────────────────────────────────────────────────────
  const testScenarios = {
    basic: [
      { id: 'basic_1', name: 'Simple Technical Support', language: 'English', query: "Hi, I can't log into my account. Can you help me reset my password?", complexity: 'Low', expectedOutcome: 'Auto-resolved with password reset steps' },
      { id: 'basic_2', name: 'Billing Question', language: 'English', query: "What's the charge of $29.99 on my credit card from last month?", complexity: 'Low', expectedOutcome: 'Auto-resolved with billing explanation' },
      { id: 'basic_3', name: 'General Inquiry', language: 'English', query: "What are your business hours? When are you open?", complexity: 'Low', expectedOutcome: 'Auto-resolved with business hours' },
      { id: 'basic_4', name: 'Product Information', language: 'English', query: "Tell me about your premium subscription plan. What features does it include?", complexity: 'Low', expectedOutcome: 'Auto-resolved with product details' }
    ],
    complex: [
      { id: 'complex_1', name: 'Angry Customer - Service Complaint', language: 'English', query: "I'VE BEEN WAITING FOR 3 WEEKS AND STILL NO RESPONSE! This is absolutely UNACCEPTABLE! I want to speak to your MANAGER RIGHT NOW!", complexity: 'High', expectedOutcome: 'Escalated to Manager due to angry tone', emotion: 'Angry' },
      { id: 'complex_2', name: 'Frustrated Customer - Multiple Issues', language: 'English', query: "Look, I've tried everything you said last time, it STILL doesn't work. First the app crashes, then I can't access my data, and now your support is telling me different things. I'm beyond frustrated!", complexity: 'High', expectedOutcome: 'Escalated to Operator - multiple unresolved issues', emotion: 'Frustrated' },
      { id: 'complex_3', name: 'Anxious Customer - Urgent Account Issue', language: 'English', query: "Oh no, oh no... someone accessed my account! I'm seeing transactions I didn't make. Please help me quickly, I'm really worried!", complexity: 'High', expectedOutcome: 'Escalated immediately - security concern', emotion: 'Anxious' },
      { id: 'complex_4', name: 'Confused Customer - Technical Jargon', language: 'English', query: "Um... so I got this error message that says something about... SSL certificate? And then it mentioned HTTPS? I don't really understand what any of that means...", complexity: 'Medium', expectedOutcome: 'AI simplifies technical explanation', emotion: 'Confused' }
    ],
    ambiguous: [
      { id: 'ambiguous_1', name: 'Vague Problem Description', language: 'English', query: "It's not working. Can you fix it?", complexity: 'Medium', expectedOutcome: 'AI asks clarifying questions' },
      { id: 'ambiguous_2', name: 'Multiple Unclear Issues', language: 'English', query: "Something's wrong with my thing, and also that other thing isn't doing what it should do, you know?", complexity: 'Medium', expectedOutcome: 'AI seeks specific details' },
      { id: 'ambiguous_3', name: 'Incomplete Information', language: 'English', query: "Can you check my account... I think there was a problem last week... or was it two weeks ago?", complexity: 'Medium', expectedOutcome: 'AI requests account details' },
      { id: 'ambiguous_4', name: 'Nonsensical Query', language: 'English', query: "The purple elephant flew yesterday when my subscription was tomorrow, please help with the blue question mark?", complexity: 'High', expectedOutcome: 'AI recognizes confusion, escalates or seeks clarification' }
    ],
    multilingual: [
      { id: 'multilingual_1', name: 'French - Simple Support', language: 'French', query: "Bonjour, j'ai besoin d'aide avec mon compte. Je ne peux pas me connecter.", complexity: 'Low', expectedOutcome: 'AI responds in French with login help' },
      { id: 'multilingual_2', name: 'French - Billing Complaint', language: 'French', query: "Je ne comprends pas pourquoi vous m'avez facturé deux fois ce mois-ci! C'est inacceptable!", complexity: 'Medium', expectedOutcome: 'AI addresses billing issue in French', emotion: 'Upset' },
      { id: 'multilingual_3', name: 'French - Technical Problem', language: 'French', query: "L'application se bloque constamment et je perds toutes mes données. Pouvez-vous résoudre ce problème urgent?", complexity: 'Medium', expectedOutcome: 'AI troubleshoots in French' },
      { id: 'multilingual_4', name: 'French - Complex Escalation', language: 'French', query: "Cela fait trois semaines que j'attends une réponse! Je veux parler à votre responsable immédiatement! Cette situation est inadmissible!", complexity: 'High', expectedOutcome: 'Escalated to Manager - maintains French', emotion: 'Angry' },
      { id: 'multilingual_5', name: 'Code-Switching (Mixed Languages)', language: 'Mixed', query: "Hello, je voudrais savoir if I can change my subscription plan? C'est possible?", complexity: 'Medium', expectedOutcome: 'AI detects primary language and responds appropriately' }
    ],
    environmental: [
      { id: 'env_1', name: 'Background Noise - Cafe', language: 'English', query: "[Background: coffee machine, chatter] Hi, can you *static* hear me? I need help with *muffled* account...", complexity: 'Medium', expectedOutcome: 'AI handles noise, asks for repetition if needed' },
      { id: 'env_2', name: 'Poor Connection - Breaking Up', language: 'English', query: "Hello? Can... *static*... hear... password... *static*... reset...", complexity: 'Medium', expectedOutcome: 'AI recognizes connection issue, offers alternative' },
      { id: 'env_3', name: 'Customer Interruption Mid-Response', language: 'English', query: "Wait, wait, hold on! Before you continue, I just remembered something else...", complexity: 'Medium', expectedOutcome: 'AI adapts to interruption gracefully' },
      { id: 'env_4', name: 'Urgent Situation - Time Pressure', language: 'English', query: "Quick! I'm about to lose my internet connection any second now. Just tell me the fastest way to download my data!", complexity: 'Medium', expectedOutcome: 'AI prioritizes critical information first' }
    ],
    empathy: [
      { id: 'empathy_1', name: 'Disappointed Customer', language: 'English', query: "I've been a loyal customer for 5 years, and this is how you treat me? I'm really disappointed...", complexity: 'Medium', expectedOutcome: 'AI shows empathy, values loyalty', emotion: 'Disappointed' },
      { id: 'empathy_2', name: 'Stressed Parent', language: 'English', query: "Sorry, my kids are screaming in the background... I'm trying to fix this before my work meeting in 10 minutes. Can you help me fast?", complexity: 'Medium', expectedOutcome: 'AI acknowledges stress, provides quick solution', emotion: 'Stressed' },
      { id: 'empathy_3', name: 'Grateful Customer', language: 'English', query: "Thank you so much for your help last time! It really saved me. Now I have another small question...", complexity: 'Low', expectedOutcome: 'AI acknowledges appreciation, maintains positive tone', emotion: 'Grateful' },
      { id: 'empathy_4', name: 'Elderly Customer - Patient Explanation Needed', language: 'English', query: "I'm sorry, I'm not very good with technology. Could you explain this step-by-step? I might need you to repeat things...", complexity: 'Medium', expectedOutcome: 'AI adapts pace, provides clear instructions', emotion: 'Uncertain' }
    ]
  };

  // ── API handlers ──────────────────────────────────────────────────────────
  const handleInitiateCall = async () => {
    unlockAudio();
    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/v1/ai-voice/simulate-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_phone: callForm.customer_phone, business_id: callForm.business_id })
      });

      if (!response.ok) throw new Error('Failed to initiate call');

      const data = await response.json();

      currentSessionRef.current = data.session;
      setCurrentSession(data.session);
      setConversationHistory(data.session.conversation_history || []);
      setCallActive(true);

      toast({
        title: t('od.callInitiated'),
        description: t('od.aiVoiceConnected', { session: data.session.session_id?.substring(0, 8) }),
      });
    } catch (error) {
      console.error('Error initiating call:', error);
      toast({
        title: t('od.callFailed'),
        description: t('od.callFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (messageText) => {
    const textToSend = typeof messageText === 'string' ? messageText : customerMessage.trim();
    if (!textToSend || !currentSessionRef.current) return;

    unlockAudio();
    if (typeof messageText !== 'string') {
      setCustomerMessage('');
    }
    setSelectedScenario(null);
    setLoading(true);
    setVoiceState('processing');
    processingRef.current = true;

    try {
      const response = await makeAuthenticatedRequest('/api/v1/ai-voice/process-conversation', {
        method: 'POST',
        body: JSON.stringify({
          session_id: currentSessionRef.current.session_id,
          customer_text: textToSend,
          ...(callForm.test_email_override ? { test_email_override: callForm.test_email_override } : {})
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to process message: ${errorText}`);
      }

      const data = await response.json();

      setConversationHistory(prev => [
        ...prev,
        {
          speaker: 'customer',
          text: textToSend,
          timestamp: new Date().toISOString()
        },
        {
          speaker: 'ai',
          text: data.ai_response,
          timestamp: new Date().toISOString(),
          metadata: {
            detected_language: data.detected_language,
            detected_intent: data.detected_intent,
            confidence: data.confidence,
            should_escalate: data.should_escalate
          }
        }
      ]);

      processingRef.current = false;

      if (data.audio_response_base64) {
        await playBase64Audio(data.audio_response_base64, data.audio_format || 'wav');
      } else {
        setVoiceState('idle');
        if (voiceModeRef.current) {
          setTimeout(() => startListeningRef.current?.(), 350);
        }
      }

      const updatedSession = {
        ...currentSessionRef.current,
        session_id: data.session_id || currentSessionRef.current.session_id,
        detected_language: data.detected_language,
        detected_intent: data.detected_intent,
        intent_confidence: data.intent_confidence
      };
      currentSessionRef.current = updatedSession;
      setCurrentSession(updatedSession);

      if (data.should_escalate) {
        toast({
          title: t('od.callEscalated'),
          description: t('od.escalatingTo', { target: data.escalate_to, reason: data.escalation_reason }),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error processing message:', error);
      processingRef.current = false;
      setVoiceState('idle');
      toast({
        title: t('od.processingFailed'),
        description: error.message || t('od.processFailedFallback'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Keep sendMessageRef current every render (safe — runs synchronously before async STT callbacks)
  sendMessageRef.current = handleSendMessage;

  const handleEndCall = () => {
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (_) {}
      try { currentSourceRef.current.disconnect(); } catch (_) {}
      currentSourceRef.current = null;
    }
    stopListening();
    voiceModeRef.current = false;
    processingRef.current = false;
    currentSessionRef.current = null;
    setVoiceMode(false);
    setVoiceState('idle');
    setInterimTranscript('');
    setCallActive(false);
    setCurrentSession(null);
    setConversationHistory([]);
    setCustomerMessage('');
    setSelectedScenario(null);
    toast({
      title: t('od.callEnded'),
      description: t('od.aiVoiceTerminated'),
    });
  };

  const loadScenario = (scenario) => {
    setSelectedScenario(scenario);
    setCustomerMessage(scenario.query);
  };

  const handleQuickTest = async (scenario) => {
    if (!callActive) {
      await handleInitiateCall();
      setTimeout(() => loadScenario(scenario), 1000);
    } else {
      loadScenario(scenario);
    }
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && customerMessage.trim()) {
        handleSendMessage();
      }
    }
  };

  // ── Voice state display helpers ───────────────────────────────────────────
  const voiceStateLabel = {
    idle: t('od.vsReady'),
    listening: t('od.listening'),
    processing: t('od.vsProcessing'),
    speaking: t('od.vsSpeaking'),
    error: t('od.vsError'),
  }[voiceState] || voiceState;

  const voiceStateBadgeClass = {
    listening: 'bg-green-500',
    processing: 'bg-blue-500',
    speaking: 'bg-purple-500',
    error: 'bg-red-500',
    idle: '',
  }[voiceState] || '';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Phone className="w-8 h-8 text-blue-600" />
          {t('nav.aiVoiceSimulator')}
        </h1>
        <p className="text-gray-600 mt-1">
          {t('od.subtitleVoiceSimulator')}
        </p>
        <Badge variant="outline" className="mt-2 text-xs" data-testid="crm-auth-status">
          {makeAuthenticatedRequest
            ? <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
            : <AlertCircle className="w-3 h-3 mr-1 text-red-500" />}
          {t('od.crmAuthReady')}
        </Badge>
      </div>

      {stateData.businessName && (
        <Card className="border-green-500 bg-green-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium">
                {t('od.calling')} <strong>{stateData.businessName}</strong>
              </span>
              <Badge variant="secondary" className="ml-auto">{stateData.phoneNumber}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Scenarios ─────────────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('od.testScenarios')}</CardTitle>
              <CardDescription>{t('od.selectScenarioVoice')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedLanguage}
                onValueChange={(v) => {
                  setSelectedLanguage(v);
                  selectedLangRef.current = LANG_CODES[v] || 'en-US';
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('od.selectLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">{t('od.langEnglish')}</SelectItem>
                  <SelectItem value="french">{t('od.langFrench')}</SelectItem>
                  <SelectItem value="mixed">{t('od.langMixed')}</SelectItem>
                </SelectContent>
              </Select>

              <Tabs defaultValue="basic" className="mt-4">
                <TabsList className="grid grid-cols-2 gap-1">
                  <TabsTrigger value="basic">{t('od.tabBasic')}</TabsTrigger>
                  <TabsTrigger value="complex">{t('od.tabComplex')}</TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-2 gap-1 mt-2">
                  <TabsTrigger value="ambiguous">{t('od.tabAmbiguous')}</TabsTrigger>
                  <TabsTrigger value="multilingual">{t('od.tabMultilingual')}</TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-2 gap-1 mt-2">
                  <TabsTrigger value="environmental">{t('od.tabEnvironment')}</TabsTrigger>
                  <TabsTrigger value="empathy">{t('od.tabEmpathy')}</TabsTrigger>
                </TabsList>

                {Object.entries(testScenarios).map(([key, scenarios]) => (
                  <TabsContent key={key} value={key} className="space-y-2 mt-4 max-h-96 overflow-y-auto">
                    {scenarios.map((scenario) => (
                      <Button
                        key={scenario.id}
                        variant="outline"
                        className="w-full justify-start text-left h-auto py-2"
                        onClick={() => handleQuickTest(scenario)}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">{scenario.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {key === 'multilingual' && <Globe className="w-3 h-3 mr-1" />}
                              {scenario.language || scenario.complexity || scenario.emotion}
                            </Badge>
                            {scenario.emotion && !['empathy'].includes(key) && (
                              <Badge className="text-xs ml-1 bg-orange-500">{scenario.emotion}</Badge>
                            )}
                          </div>
                        </div>
                        <PlayCircle className="w-4 h-4" />
                      </Button>
                    ))}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* ── Right: Controls + Conversation ──────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('od.callControls')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('od.customerName')}</Label>
                  <Input
                    value={callForm.customer_name}
                    onChange={(e) => setCallForm({ ...callForm, customer_name: e.target.value })}
                    disabled={callActive}
                  />
                </div>
                <div>
                  <Label>{t('od.phoneNumber')}</Label>
                  <Input
                    value={callForm.customer_phone}
                    onChange={(e) => setCallForm({ ...callForm, customer_phone: e.target.value })}
                    disabled={callActive}
                  />
                </div>
              </div>
              <div>
                <Label>{t('od.testEmailOverride')} <span className="text-xs text-gray-400 font-normal">{t('od.optionalSimulatorOnly')}</span></Label>
                <Input
                  type="email"
                  placeholder={t('od.emailOverridePlaceholder')}
                  value={callForm.test_email_override}
                  onChange={(e) => setCallForm({ ...callForm, test_email_override: e.target.value })}
                  disabled={callActive}
                />
              </div>
              <div className="flex gap-2">
                {!callActive ? (
                  <Button onClick={handleInitiateCall} disabled={loading} className="flex-1">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
                    {t('od.initiateCall')}
                  </Button>
                ) : (
                  <Button onClick={handleEndCall} variant="destructive" className="flex-1">
                    <PhoneOff className="mr-2 h-4 w-4" />
                    {t('od.endCall')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {selectedScenario && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{t('od.selectedScenario')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge>{selectedScenario.language}</Badge>
                  <Badge variant="outline">{selectedScenario.complexity}</Badge>
                  {selectedScenario.emotion && (
                    <Badge className="bg-orange-500">{selectedScenario.emotion}</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-600">
                  <strong>{t('od.expected')}</strong> {selectedScenario.expectedOutcome}
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('od.conversation')}</span>
                <div className="flex gap-2">
                  <Badge variant="outline">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {conversationHistory.length}
                  </Badge>
                  {loading && (
                    <Badge variant="outline">
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      {t('od.processing')}
                    </Badge>
                  )}
                  {callActive ? (
                    <Badge className="bg-green-500">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t('od.active')}
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {t('od.idle')}
                    </Badge>
                  )}
                </div>
              </CardTitle>
              {currentSession && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge variant="outline">{t('od.sessionLabel')} {currentSession.session_id?.substring(0, 8)}</Badge>
                  <Badge variant="outline">{t('od.languageLabel')} {currentSession.detected_language || t('od.detecting')}</Badge>
                  {currentSession.detected_intent && (
                    <Badge variant="outline">{t('od.intentLabel')} {currentSession.detected_intent}</Badge>
                  )}
                </div>
              )}
            </CardHeader>

            {/* Fixed-height card content with internal scroll */}
            <CardContent className="flex flex-col p-4" style={{ height: '480px' }}>
              {!callActive ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                  <Phone className="w-12 h-12 mb-4 text-gray-400" />
                  <p>{t('od.initiateCallToStart')}</p>
                </div>
              ) : (
                <>
                  {/* Scrollable message area */}
                  <div className="flex-1 overflow-y-auto space-y-3 min-h-0 mb-3">
                    {conversationHistory.map((turn, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-2 ${turn.speaker === 'customer' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-lg ${
                            turn.speaker === 'customer'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {turn.speaker === 'customer'
                              ? <User className="w-4 h-4" />
                              : <Bot className="w-4 h-4" />}
                            <span className="text-xs font-semibold">
                              {turn.speaker === 'customer' ? t('od.customer') : t('od.aiAgent')}
                            </span>
                          </div>
                          <p className="text-sm">{turn.text}</p>
                          {turn.metadata && (
                            <div className="mt-2 flex gap-1 flex-wrap">
                              {turn.metadata.detected_language && (
                                <Badge variant="secondary" className="text-xs">{turn.metadata.detected_language}</Badge>
                              )}
                              {turn.metadata.confidence && (
                                <Badge variant="secondary" className="text-xs">{(turn.metadata.confidence * 100).toFixed(0)}%</Badge>
                              )}
                              {turn.metadata.should_escalate && (
                                <Badge className="bg-red-500 text-xs">{t('od.escalated')}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Interim STT transcript */}
                    {interimTranscript && (
                      <div className="flex gap-2 justify-end">
                        <div className="max-w-[80%] p-3 rounded-lg bg-blue-200 text-blue-900 italic">
                          <div className="flex items-center gap-2 mb-1">
                            <Mic className="w-4 h-4 animate-pulse" />
                            <span className="text-xs font-semibold">{t('od.listening')}</span>
                          </div>
                          <p className="text-sm">{interimTranscript}</p>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Sticky input area */}
                  <div className="flex-shrink-0 space-y-2 border-t pt-3">
                    {/* Voice controls row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Mic button — single-turn push-to-listen */}
                      <button
                        type="button"
                        title={t('od.clickToSpeak')}
                        onClick={() => {
                          unlockAudio();
                          startListening();
                        }}
                        disabled={voiceState === 'processing' || loading}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition-colors
                          ${voiceState === 'listening'
                            ? 'bg-green-100 border-green-500 text-green-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {voiceState === 'listening'
                          ? <Mic className="w-3 h-3 animate-pulse" />
                          : <Mic className="w-3 h-3" />}
                        {t('od.customer')}
                      </button>

                      {/* Voice mode toggle button */}
                      <button
                        type="button"
                        title={voiceMode ? t('od.turnOffVoiceMode') : t('od.turnOnVoiceMode')}
                        onClick={toggleVoiceMode}
                        disabled={voiceState === 'processing' || loading}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition-colors
                          ${voiceMode
                            ? 'bg-purple-100 border-purple-500 text-purple-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-600'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {voiceMode
                          ? <Volume2 className="w-3 h-3 animate-pulse" />
                          : <Volume2 className="w-3 h-3" />}
                        {t('od.aiVoice')} {voiceMode ? t('od.on') : t('od.off')}
                      </button>

                      {/* Voice state indicator */}
                      {voiceState !== 'idle' && (
                        <Badge className={`text-xs ${voiceStateBadgeClass}`}>
                          {voiceState === 'listening' && <Mic className="w-3 h-3 mr-1 animate-pulse" />}
                          {voiceState === 'speaking' && <Volume2 className="w-3 h-3 mr-1" />}
                          {voiceState === 'processing' && <Zap className="w-3 h-3 mr-1" />}
                          {voiceStateLabel}
                        </Badge>
                      )}

                      {loading && (
                        <Badge className="bg-blue-500 text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          {t('od.thinking')}
                        </Badge>
                      )}
                    </div>

                    {/* Text input */}
                    <Textarea
                      ref={textareaRef}
                      placeholder={t('od.voiceInputPlaceholder')}
                      value={customerMessage}
                      onChange={(e) => setCustomerMessage(e.target.value)}
                      onKeyDown={handleTextareaKeyDown}
                      rows={2}
                      className="resize-none"
                    />
                    <Button
                      onClick={() => handleSendMessage()}
                      disabled={loading || !customerMessage.trim()}
                      className="w-full"
                    >
                      {loading
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Send className="mr-2 h-4 w-4" />}
                      {t('od.sendMessage')}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIVoiceSimulatorEnhanced;
